/**
 * Servicio de casos de éxito — lee desde Realtime Database, nodo `/cases`.
 *
 * Estructura esperada en RTDB:
 *
 *   /cases/
 *     $caseId/
 *       badge:             string   (requerido)
 *       client:            string   (requerido)
 *       title:             string   (requerido)
 *       result:            string   (requerido)
 *       stack:             string[] (requerido — chips de tecnologías)
 *       challenge:         string   (opcional — detalle largo: contexto / reto)
 *       solution:          string   (opcional — detalle largo: solución)
 *       impact:            string   (opcional — detalle largo: impacto medible)
 *       highlights:        string[] (opcional — bullets destacados en el modal)
 *       illustrationIndex: number   (opcional — 0..8, fija la ilustración SVG)
 *       order:             number   (requerido — orden ascendente)
 *       active:            boolean  (requerido — solo se muestran los `true`)
 *
 * Reglas en `database.rules.json`: `/cases` con `.read: true` y `.write: false`.
 */
import { ref, onValue, query, orderByChild } from 'firebase/database';

import type { CaseStudy, CaseStudySnapshot } from '../types/domain';

import { db } from './firebaseConfig';

export type CasesSubscriptionCallback = (cases: CaseStudy[], error: Error | null) => void;
export type Unsubscribe = () => void;

function toStringArray(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.filter((v): v is string => typeof v === 'string' && v.length > 0);
  }
  if (value && typeof value === 'object') {
    return Object.values(value as Record<string, unknown>)
      .filter((v): v is string => typeof v === 'string' && v.length > 0);
  }
  return [];
}

/**
 * Normaliza un snapshot de Firebase a un array de casos listo para renderear.
 *
 * - Tolera nodo vacío o ausente devolviendo `[]`.
 * - Solo incluye casos con `active !== false` y `title` no vacío.
 * - Ordena por `order` ascendente; los casos sin `order` van al final.
 */
export function normalizeCasesSnapshot(value: unknown): CaseStudy[] {
  if (!value || typeof value !== 'object') return [];

  const record = value as Record<string, CaseStudySnapshot>;

  const entries = Object.entries(record).map(([id, raw], idx) => ({
    id,
    badge: raw?.badge ?? '',
    client: raw?.client ?? '',
    title: raw?.title ?? '',
    result: raw?.result ?? '',
    stack: toStringArray(raw?.stack),
    challenge: raw?.challenge ?? '',
    solution: raw?.solution ?? '',
    impact: raw?.impact ?? '',
    highlights: toStringArray(raw?.highlights),
    illustrationIndex:
      typeof raw?.illustrationIndex === 'number' ? raw.illustrationIndex : idx,
    order: typeof raw?.order === 'number' ? raw.order : Number.POSITIVE_INFINITY,
    active: raw?.active !== false,
  }));

  return entries
    .filter((c) => c.active && c.title)
    .sort((a, b) => a.order - b.order);
}

/** Se suscribe al nodo `/cases` y notifica al callback cada vez que cambia. */
export function subscribeToCases(cb: CasesSubscriptionCallback): Unsubscribe {
  const casesRef = query(ref(db, '/cases'), orderByChild('order'));
  const unsubscribe = onValue(
    casesRef,
    (snapshot) => {
      try {
        const cases = normalizeCasesSnapshot(snapshot.val());
        cb(cases, null);
      } catch (err) {
        cb([], err as Error);
      }
    },
    (err) => cb([], err),
  );
  return unsubscribe;
}
